import { ComponentPropsWithoutRef, FC, ReactNode, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { mergeTailwindClasses } from '@kontentino/ui';

const Drawer: FC<ComponentPropsWithoutRef<typeof DialogPrimitive.Root>> = ({
  open,
  ...props
}) => {
  // Radix's dismissable layer can leave `pointer-events: none` on <body> after
  // the drawer closes, blocking all clicks until reload. Clear it on close.
  useEffect(() => {
    if (!open) {
      document.body.style.pointerEvents = '';
    }
  }, [open]);

  return <DialogPrimitive.Root open={open} {...props} />;
};

const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerClose = DialogPrimitive.Close;

interface DrawerContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string;
  overlayDataCy?: string;
}

const DrawerContent: FC<DrawerContentProps> = ({
  className,
  overlayClassName,
  overlayDataCy,
  children,
  ...props
}) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      data-slot="drawer-overlay"
      data-cy={overlayDataCy}
      className={mergeTailwindClasses(
        'tw-fixed tw-inset-0 tw-z-50 tw-bg-black/50 tw-backdrop-blur-sm',
        overlayClassName,
      )}
    />
    <DialogPrimitive.Content
      data-slot="drawer-content"
      className={mergeTailwindClasses(
        'tw-fixed tw-right-4 tw-top-4 tw-z-50 tw-flex tw-h-[calc(100%-32px)] tw-w-[400px] tw-max-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-white tw-shadow-xl tw-outline-none',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
);

const DrawerHeader: FC<{ className?: string; children: ReactNode }> = ({
  className,
  children,
}) => (
  <div
    className={mergeTailwindClasses(
      'tw-flex tw-items-center tw-justify-between tw-p-4',
      className,
    )}
  >
    {children}
  </div>
);

const DrawerFooter: FC<{ className?: string; children: ReactNode }> = ({
  className,
  children,
}) => (
  <div
    className={mergeTailwindClasses(
      'tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-t-grayscale-30 tw-p-4',
      className,
    )}
  >
    {children}
  </div>
);

const DrawerTitle: FC<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
> = ({ className, ...props }) => (
  <DialogPrimitive.Title
    className={mergeTailwindClasses('tw-text-lg tw-font-semibold', className)}
    {...props}
  />
);

const DrawerDescription: FC<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
> = ({ className, ...props }) => (
  <DialogPrimitive.Description
    className={mergeTailwindClasses(
      'tw-text-sm tw-text-grayscale-100',
      className,
    )}
    {...props}
  />
);

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
